import { useEffect, useState, useCallback } from 'react';
import cronService from '../services/cronService';
import authService from '../services/authService';

export function useCronTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const currentUser = authService.getCurrentUser();
  const isAdmin = (currentUser?.role === 'admin');
  const isSuper = (currentUser?.role === 'superuser');

  const normalizeOrg = (u) => (u?.organisation ?? u?.organization ?? u?.org ?? u?.orgId ?? u?.org_id ?? null);

  const scopeTasks = useCallback((list) => {
    if (!Array.isArray(list)) return [];
    if (isAdmin) return list; // admins see all
    const myId = currentUser?.id;
    const myOrg = normalizeOrg(currentUser);
    return list.filter((t) => {
      const createdBy = t?.createdBy ?? t?.created_by ?? t?.owner_id ?? t?.owner;
      const org = t?.organisation ?? t?.organization ?? t?.org ?? t?.orgId ?? t?.org_id;
      if (isSuper) {
        // Superuser: scope to organization if present; otherwise fallback to creator id
        if (org != null && myOrg != null) return String(org) === String(myOrg);
        if (createdBy != null && myId != null) return String(createdBy) === String(myId);
        return false;
      }
      // Regular user: only own tasks
      if (createdBy != null && myId != null) return String(createdBy) === String(myId);
      return false;
    });
  }, [currentUser, isAdmin, isSuper]);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await cronService.getTasks();
      setTasks(scopeTasks(all));
    } catch (err) {
      setError(err.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [scopeTasks]);

  const createTask = async (taskData) => {
    setError(null);
    try {
      const task = await cronService.createTask(taskData);
      setTasks((prev) => scopeTasks([...(prev || []), task]));
      return task;
    } catch (err) {
      setError(err.message || 'Failed to create task');
      throw err;
    }
  };

  const updateTask = async (taskId, taskData) => {
    setError(null);
    try {
      const task = await cronService.updateTask(taskId, taskData);
      setTasks((prev) => scopeTasks((prev || []).map((t) => (t.id === task.id ? task : t))));
      return task;
    } catch (err) {
      setError(err.message || 'Failed to update task');
      throw err;
    }
  };

  const deleteTask = async (taskId) => {
    setError(null);
    try {
      await cronService.deleteTask(taskId);
      setTasks((prev) => (prev || []).filter((t) => t.id !== taskId));
      return true;
    } catch (err) {
      setError(err.message || 'Failed to delete task');
      throw err;
    }
  };

  const toggleTask = async (taskId, enabled) => {
    setError(null);
    try {
      const task = await cronService.toggleTask(taskId, enabled);
      setTasks((prev) => scopeTasks((prev || []).map((t) => (t.id === task.id ? task : t))));
      return task;
    } catch (err) {
      setError(err.message || 'Failed to toggle task');
      throw err;
    }
  };

  const triggerTask = async (taskId) => {
    setError(null);
    try {
      await cronService.triggerTask(taskId);
      return true;
    } catch (err) {
      setError(err.message || 'Failed to trigger task');
      throw err;
    }
  };

  useEffect(() => { loadTasks(); }, [loadTasks]);

  return { tasks, loading, error, reload: loadTasks, createTask, updateTask, deleteTask, toggleTask, triggerTask };
}

export function useCronExecutions(taskId, initialLimit = 50) {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(initialLimit);

  const loadExecutions = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await cronService.getExecutions(taskId, limit);
      setExecutions(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load executions');
    } finally {
      setLoading(false);
    }
  }, [taskId, limit]);

  useEffect(() => { loadExecutions(); }, [loadExecutions]);

  return { executions, loading, error, loadMore: () => setLimit((l) => l + initialLimit) };
}

export function useCronExpression() {
  const [isValid, setIsValid] = useState(true);
  const [description, setDescription] = useState('');

  const validate = useCallback((expr) => {
    try {
      const valid = cronService.validateCronExpression(expr);
      setIsValid(valid);
      setDescription(valid ? cronService.describeCronExpression(expr) : '');
      return valid;
    } catch (_) {
      setIsValid(false);
      setDescription('');
      return false;
    }
  }, []);

  const presets = cronService.getPresetExpressions();

  return { isValid, description, validate, presets };
}